import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "../cn.js";
import { Info } from "../icons.js";

/**
 * A small ⓘ button that reveals help text in a popover — on hover/focus for
 * mouse & keyboard, and on tap for touch (tap again, tap outside, or Esc to
 * dismiss). Keeps long descriptions from cluttering settings surfaces,
 * especially on mobile. The panel anchors below-left of the icon, so place the
 * tip right after a left-aligned heading.
 */
export function InfoTip({
  children,
  label = "More information",
  className,
}: {
  children: ReactNode;
  label?: string;
  className?: string;
}) {
  const [pinned, setPinned] = useState(false);
  const [hover, setHover] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!pinned) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setPinned(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPinned(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pinned]);

  const visible = pinned || hover;

  return (
    // pointerenter/leave treat descendants as "inside", so moving from the icon
    // down onto the (absolutely-positioned, DOM-child) panel keeps it open.
    <span
      ref={ref}
      className={cn("relative inline-flex align-middle", className)}
      onPointerEnter={(e) => { if (e.pointerType === "mouse") setHover(true); }}
      onPointerLeave={(e) => { if (e.pointerType === "mouse") setHover(false); }}
    >
      <button
        type="button"
        aria-label={label}
        aria-expanded={visible}
        onClick={() => setPinned((v) => !v)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {visible && (
        <span role="tooltip" className="absolute left-0 top-full z-50 pt-1">
          <span className="block w-64 max-w-[calc(100vw-2.5rem)] rounded-md border bg-card p-3 text-left text-sm font-normal normal-case leading-snug tracking-normal text-muted-foreground shadow-md sm:w-96 sm:max-w-[min(28rem,calc(100vw-2.5rem))]">
            {children}
          </span>
        </span>
      )}
    </span>
  );
}
